import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useHOA } from '../contexts/HOAContext';
import { logEvent } from '../lib/activityLogger';
import {
  arrivedViaInvite,
  clearInviteArrival,
  getInviteArrival,
  getInviteTokenFromUrl,
} from '../lib/inviteDetection';
import { withOperationTimeout, withSupabaseQueryTimeout } from '../lib/supabaseTimeout';
import { OnboardingForm } from '../components/onboarding/OnboardingForm';
import { InviteStatusCard } from '../components/onboarding/InviteStatusCard';
import { InviteTokenOnboarding } from '../components/onboarding/InviteTokenOnboarding';

/**
 * WelcomePage — onboarding entry point.
 *
 *  - /welcome?invite_token=…  App-owned invitation (the normal path). Handled
 *    entirely by InviteTokenOnboarding: preview, then one redeem POST.
 *  - Signed-in user with onboarding_complete !== true and no token: the
 *    original onboarding form (profile + password + invitation activation).
 *    This protects accounts created before app-owned invites.
 *  - Retired Supabase OTP links (token_hash): "ask for a fresh invitation" card.
 */

const getInviteVerificationParams = () => {
  if (typeof window === 'undefined') {
    return { tokenHash: '', type: '', hasOnboardingMarker: false };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    tokenHash: params.get('token_hash') || '',
    type: params.get('type') || '',
    hasOnboardingMarker: params.get('onboarding') === '1',
  };
};

const removeInviteSecretsFromUrl = () => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  ['token_hash', 'type', 'error', 'error_code', 'error_description'].forEach((key) =>
    url.searchParams.delete(key)
  );
  url.searchParams.set('onboarding', '1');
  url.hash = '';
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
};

const buildActivationIssue = ({ arrival, retiredLink, hasOnboardingMarker }) => {
  const hadLinkProblem = Boolean(retiredLink || arrival?.errorCode || arrival?.errorDescription);

  if (hadLinkProblem) {
    return {
      title: "This Invitation Link Isn't Active",
      description:
        'Invitation links are replaced whenever the board sends a new one. Please ask the HOA board for a fresh invitation — it only takes a moment.',
    };
  }

  if (hasOnboardingMarker || arrivedViaInvite()) {
    return {
      title: 'This Activation Link Is Incomplete',
      description:
        'The page opened, but the secure details were missing. Please ask the HOA board to send you a fresh invitation; you do not need an existing password.',
    };
  }

  return {
    title: 'Invitation Needed',
    description:
      'This page is for new members setting up their account. Please open a current invitation email from the HOA board.',
  };
};

const SignedInOnboarding = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { hoa } = useHOA();

  const [authEmail, setAuthEmail] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [checkingSession, setCheckingSession] = useState(true);
  const [activationIssue, setActivationIssue] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Email and linked properties are informational. Personal identity fields
  // intentionally start blank so household/legal-owner roster labels never
  // become a human portal user's name or phone by accident.
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();
        if (!mounted) return;

        const verificationParams = getInviteVerificationParams();
        const arrival = getInviteArrival();

        // Retired Supabase OTP invitation links (token_hash & type=invite) are
        // no longer verified. They get the same "ask for a fresh invitation"
        // card as any inactive link; the secrets are removed from the URL.
        const isRetiredOtpLink = Boolean(verificationParams.tokenHash);
        if (isRetiredOtpLink) removeInviteSecretsFromUrl();

        if (!authUser) {
          setActivationIssue(
            buildActivationIssue({
              arrival,
              retiredLink: isRetiredOtpLink,
              hasOnboardingMarker: verificationParams.hasOnboardingMarker,
            })
          );
          setCheckingSession(false);
          return;
        }
        if (authUser.user_metadata?.onboarding_complete === true) {
          clearInviteArrival();
          navigate('/portal', { replace: true });
          return;
        }
        setAuthEmail(authUser.email || '');

        try {
          // Two-step lookup: members read addresses through the
          // column-restricted directory_properties view, not the base table.
          const { data: occupants } = await supabase
            .from('property_occupants')
            .select('property_id')
            .eq('user_id', authUser.id)
            .eq('is_active', true);
          const propertyIds = (occupants || []).map((o) => o.property_id).filter(Boolean);
          if (mounted && propertyIds.length > 0) {
            const { data: props } = await supabase
              .from('directory_properties')
              .select('id, situs_address')
              .in('id', propertyIds);
            const addresses = (props || []).map((p) => p.situs_address).filter(Boolean);
            if (mounted) setPropertyAddress(addresses.join(' · '));
          }
        } catch (e) {
          /* property display is optional — never block onboarding */
        }
      } catch (e) {
        /* fall through to the no-session state */
      }
      if (mounted) setCheckingSession(false);
    };
    init();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const handleSubmit = async ({ firstName, lastName, phone, password }) => {
    setSubmitting(true);
    setSubmitError('');
    const fullName = `${firstName} ${lastName}`;

    try {
      // 1) Update the member profile before marking onboarding complete.
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) {
        throw new Error('Your session ended. Please sign in again to finish setting up.');
      }

      const { data: updatedProfile, error: profileError } = await withSupabaseQueryTimeout(
        () =>
          supabase
            .from('user_profiles')
            .update({
              full_name: fullName,
              first_name: firstName,
              last_name: lastName,
              phone,
            })
            .eq('id', authUser.id)
            .select('id')
            .maybeSingle(),
        'Saving your profile',
        30000
      );
      if (profileError || !updatedProfile) {
        throw new Error('Your profile could not be saved. Please try again.');
      }

      // 2) Activate the invitation before marking onboarding complete. This is
      // idempotent and links property users while leaving non-property users unlinked.
      const { data: activationSummary, error: activationError } = await withSupabaseQueryTimeout(
        () => supabase.rpc('mark_my_invitation_group_activated'),
        'Activating your invitation',
        30000
      );
      if (activationError) {
        throw new Error('Your activation could not be finalized. Please try again.');
      }
      const expectedPropertyCount = Number(activationSummary?.expected_property_count ?? 0);
      const linkedPropertyCount = Number(activationSummary?.linked_property_count ?? 0);
      if (activationSummary?.verified !== true || expectedPropertyCount !== linkedPropertyCount) {
        throw new Error(
          'Your properties could not all be linked. No access was lost; please try again.'
        );
      }

      // 3) Set the password and publish completion metadata in one auth update.
      const { data: authData, error: authError } = await withOperationTimeout(
        () =>
          supabase.auth.updateUser({
            password,
            data: { onboarding_complete: true, full_name: fullName },
          }),
        'Securing your account',
        30000,
        { retries: 0 }
      );
      if (authError) {
        if ((authError.message || '').toLowerCase().includes('password')) {
          throw new Error('Password must be at least 8 characters');
        }
        throw new Error('Something went wrong. Please try again.');
      }
      if (authData?.user?.user_metadata?.onboarding_complete !== true) {
        throw new Error('Your account was saved, but setup did not finish. Please try again.');
      }

      logEvent('profile_updated', { source: 'onboarding_welcome' });
      clearInviteArrival();

      // A replace reload gives AuthContext a clean read of the updated role and
      // metadata without leaving the one-time welcome page in browser history.
      window.location.replace('/portal');
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  if (isLoading || checkingSession) {
    return (
      <main className="min-h-screen bg-main-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
      </main>
    );
  }

  // No session — explain whether the page was opened directly or a link
  // arrived without usable verification details. Never send a new user with
  // no password into a sign-in loop.
  if (!isAuthenticated && !authEmail) {
    const supportEmail = hoa?.support_email || 'support@atrionos.com';
    const issue = activationIssue || buildActivationIssue({});
    return (
      <InviteStatusCard
        icon="mail"
        title={issue.title}
        body={issue.description}
        actions={[
          ...(supportEmail
            ? [
                {
                  label: 'Request a New Invitation',
                  href: `mailto:${supportEmail}?subject=${encodeURIComponent('Please resend my portal invitation')}`,
                  variant: 'primary',
                },
              ]
            : []),
          { label: 'Return to Website', to: '/', variant: supportEmail ? 'link' : 'ghost' },
        ]}
      />
    );
  }

  return (
    <OnboardingForm
      hoaName={hoa?.name}
      email={authEmail}
      propertyAddress={propertyAddress}
      submitting={submitting}
      submitError={submitError}
      onSubmit={handleSubmit}
    />
  );
};

const WelcomePage = () => {
  // Read once: the token is removed from the URL after a successful redemption.
  const [inviteToken] = useState(() => getInviteTokenFromUrl());
  if (inviteToken) return <InviteTokenOnboarding token={inviteToken} />;
  return <SignedInOnboarding />;
};

export default WelcomePage;
